import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate
} from "@angular/material/datepicker";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {ICharity} from "../../interfaces/charity/ICharity";
import {EmailConfirmationService} from "../../services/email-confirmation.service";
import {ActiveUserService} from "../../services/active-user.service";
import {IEmailConfirmationFilter} from "../../interfaces/IEmailConfirmationFilter";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {DateTime} from "luxon";
import {IEmailConfirmationPagedResponse} from "../../interfaces/IEmailConfirmationPagedResponse";
import {IEmailConfirmation} from "../../interfaces/IEmailConfirmation";
import {map, Observable} from "rxjs";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {AgGridAngular} from "ag-grid-angular";
import {AsyncPipe, DatePipe, NgClass, NgIf} from "@angular/common";
import {ContainerComponent} from "@coreui/angular";

@Component({
  selector: 'app-player-email-confirmations',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerToggle,
    MatEndDate,
    MatHint,
    MatLabel,
    MatError,
    MatStartDate,
    MatSuffix,
    MatButton,
    MatIcon,
    MatTooltip,
    AgGridAngular,
    AsyncPipe,
    NgClass,
    NgIf,
    ContainerComponent
  ],
  templateUrl: './player-email-confirmations.component.html',
  styleUrl: './player-email-confirmations.component.css'
})
export class PlayerEmailConfirmationsComponent implements OnInit {

  public pagedEmailConfirmationResponse: IEmailConfirmationPagedResponse | null = null;
  public emailConfirmations$: Observable<IEmailConfirmation[]> = this.emailConfirmationService.emailConfirmations$.pipe(
    map((response) => {
      this.pagedEmailConfirmationResponse = response;
      this.updatePaginator();
      return response.Data;
    })
  )

  public confirmationTypes = [
    {
      name: "Registration",
      value: 1
    },
    {
      name: "Password Recovery",
      value: 2
    },
    {
      name: "Email Update",
      value: 3
    },
    {
      name: "Game Admin Registration",
      value: 4
    },
    {
      name: "Organization Admin Registration",
      value: 5
    },
    {
      name: "Causable Admin Registration",
      value: 6
    },
    {
      name: "Admin Password Recovery",
      value: 7
    },
    {
      name: "Admin Email Update",
      value: 8
    },
    {
      name: "Charity Admin Registration",
      value: 9
    },
    {
      name: "Cross Game Admin Invite",
      value: 10
    }
  ];

  public colDefs: ColDef[] = [
    {
    headerName: "Id",
    field: "Id",
    resizable: true,
    sortable: true
    },
    {
      headerName: "Email",
      field: "Email",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Type",
      field: "EmailConfirmationType",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        return this.confirmationTypes[params.value - 1].name;
      }
    },
    {
      headerName: "Code",
      field: "Code",
      resizable: true,
      sortable: true
    },
    {
      headerName: "Created On",
      field: "CreatedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: "Confirmed On",
      field: "ConfirmedOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: "Expires On",
      field: "ExpiresOn",
      resizable: true,
      sortable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y');
        return convertedDate ? convertedDate : '';
      }
    },
    {
      headerName: "Charity Id",
      field: "CharityIdFromQrInvite",
      resizable: true,
      sortable: true
    }
  ];

  emailControl = new FormControl<string | null>("", Validators.email);
  typeControl = new FormControl<number | null>(null);
  fromDateControl = new FormControl(DateTime.now().minus({week: 2}).toString());
  toDateControl = new FormControl(DateTime.now().plus({day: 1}).toString());
  confirmedControl = new FormControl<boolean | null>(null);

  public filterForm = new FormGroup({
    email : this.emailControl,
    type: this.typeControl,
    fromDate: this.fromDateControl,
    toDate: this.toDateControl,
    confirmed: this.confirmedControl
  });

  public uiState = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public gridApi!: GridApi;

  fromNumber: number = 0;
  toNumber: number = 0;
  totalRecords: number = 0;
  pageNumber: number = 0;
  totalPages: number = 0;

  public charities: ICharity[] = [];

  constructor(private errorHandlingService: ErrorHandlingService,
              private emailConfirmationService: EmailConfirmationService,
              private activeUserService: ActiveUserService,
              private datePipe: DatePipe) {
  }

  ngOnInit(): void {
    this.getPaginatedResponse(1);
  }

  getPaginatedResponse(pageNumberP: number) {
    this.uiState = UIStateEnum.ShowLoading;

    let filter : IEmailConfirmationFilter = {
      email: this.emailControl.value!,
      type: this.typeControl.value!,
      toDate: this.toDateControl.value!,
      fromDate: this.fromDateControl.value!,
      confirmed: this.confirmedControl.value!,
      pageSize: 15,
      pageNumber: pageNumberP
    };

    this.emailConfirmationService.getEmailConfirmations(filter).subscribe({
      next: () => {
        this.uiState = UIStateEnum.ShowData;
      },
      error: err => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err, "Failed to fetch emails");
      }
    })
  }

  onFormSubmit() {
    this.getPaginatedResponse(1);
  }

  onResetClick() {
    this.emailControl.setValue("");
    this.typeControl.setValue(null);
    this.fromDateControl.setValue(DateTime.now().minus({week: 2}).toString());
    this.toDateControl.setValue(DateTime.now().plus({day: 1}).toString());
    this.confirmedControl.setValue(null);
    this.getPaginatedResponse(1);
  }

  updatePaginator() {
    if (!this.pagedEmailConfirmationResponse) return;

    this.pageNumber = this.pagedEmailConfirmationResponse.PageNumber;
    this.totalPages = this.pagedEmailConfirmationResponse.TotalPages;
    this.totalRecords = this.pagedEmailConfirmationResponse.TotalRecords;

    let toNumber = this.pageNumber * this.pagedEmailConfirmationResponse.PageSize;
    this.toNumber = Math.min(toNumber, this.totalRecords);
    this.fromNumber = Math.max(1, this.toNumber - this.pagedEmailConfirmationResponse.PageSize + 1);
  }

  onGridReady(paramsP: GridReadyEvent<IEmailConfirmation>) {
    this.gridApi = paramsP.api;
    this.gridApi.sizeColumnsToFit({defaultMinWidth: 90});
  }

  onBtFirst() {
    if (this.pageNumber === 1) {
      return;
    }

    this.getPaginatedResponse(1);
  }

  onBtLast() {
    if ((this.pageNumber + 1) > this.totalPages) {
      return;
    }

    this.getPaginatedResponse((this.pagedEmailConfirmationResponse?.TotalPages!));
  }

  onBtNext() {
    if ((this.pageNumber + 1) > this.totalPages) {
      return;
    }

    this.getPaginatedResponse(this.pageNumber + 1);
  }

  onBtPrevious() {
    if (this.pageNumber === 1) {
      return;
    }

    this.getPaginatedResponse(this.pageNumber - 1);
  }
}
